import { useCallback, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { AppShellContext } from '@/components/layout/AppShell';
import { useProjectsApi, useWarehouseApi } from '@/services/api';
import { t } from '@/i18n';
import type { CreateWarehouseItemDto, ProjectOption, WarehouseItem } from '@ip/shared-types';

const COLUMN_COUNT = 7;

// Sentinel radix Select value for "no project" (empty string is not allowed).
const NO_PROJECT = 'none';
const DEFAULT_COLOR = '#808080';

interface ItemFormValues {
  code: string;
  color: string;
  name: string;
  projectId: string;
  invoiceDescription: string;
  unitPriceNet: string;
}

const EMPTY_FORM: ItemFormValues = {
  code: '',
  color: DEFAULT_COLOR,
  name: '',
  projectId: '',
  invoiceDescription: '',
  unitPriceNet: '',
};

/**
 * Katalog Pozycji: table (code/color/name/project/invoice description/net
 * price) backed by a server-side search, with a single Dialog shared by
 * add + edit (form fields identical either way) and an AlertDialog delete
 * confirm carrying the "existing WZ keep their data" warning.
 */
export function PozycjeCatalog() {
  const { nip } = useOutletContext<AppShellContext>();
  const { listItems, createItem, updateItem, deleteItem } = useWarehouseApi();
  const { listProjectOptions } = useProjectsApi();

  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [items, setItems] = useState<WarehouseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [search, setSearch] = useState('');
  const loadSeq = useRef(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<WarehouseItem | null>(null);
  const [form, setForm] = useState<ItemFormValues>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<WarehouseItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      const result = await listItems(nip, search || undefined);
      if (seq !== loadSeq.current) return;
      setItems(result);
      setForbidden(false);
    } catch (error) {
      if (seq !== loadSeq.current) return;
      if ((error as { status?: number })?.status === 403) {
        setForbidden(true);
      } else {
        toast.error(t('errors.loadingFailed'));
      }
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [nip, search, listItems]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // Dropdown Projektów — awaria selektora nie blokuje katalogu (pusta lista).
  useEffect(() => {
    let cancelled = false;
    listProjectOptions(nip)
      .then((rows) => {
        if (!cancelled) setProjects(rows);
      })
      .catch(() => {
        if (!cancelled) setProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [nip, listProjectOptions]);

  const openAdd = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (item: WarehouseItem) => {
    setEditingItem(item);
    setForm({
      code: item.code,
      color: item.color,
      name: item.name,
      projectId: item.projectId ?? '',
      invoiceDescription: item.invoiceDescription ?? '',
      unitPriceNet: item.unitPriceNet !== null ? String(item.unitPriceNet) : '',
    });
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    const code = form.code.trim();
    const color = form.color.trim();
    const name = form.name.trim();
    if (!code) {
      toast.error(t('magazyn.items.codeRequired'));
      return;
    }
    if (!color) {
      toast.error(t('magazyn.items.colorRequired'));
      return;
    }
    if (!name) {
      toast.error(t('magazyn.items.nameRequired'));
      return;
    }

    let unitPriceNet: number | null = null;
    if (form.unitPriceNet.trim()) {
      const parsed = Number(form.unitPriceNet);
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error(t('magazyn.items.priceInvalid'));
        return;
      }
      unitPriceNet = parsed;
    }

    const dto: CreateWarehouseItemDto = {
      code,
      color,
      name,
      projectId: form.projectId || null,
      invoiceDescription: form.invoiceDescription.trim() || null,
      unitPriceNet,
    };

    setSubmitting(true);
    try {
      if (editingItem) {
        await updateItem(nip, editingItem.id, dto);
      } else {
        await createItem(nip, dto);
      }
      setFormOpen(false);
      setEditingItem(null);
      setForm(EMPTY_FORM);
      await load();
      toast.success(t('magazyn.items.saveSuccess'));
    } catch (error) {
      // Backend returns 409 for a duplicate code — localized message so it
      // matches the UI language regardless of what the API sent.
      if ((error as { status?: number })?.status === 409) {
        toast.error(t('magazyn.items.duplicateCode'));
      } else {
        toast.error(error instanceof Error && error.message ? error.message : t('magazyn.items.saveFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteItem(nip, deleteTarget.id);
      setDeleteTarget(null);
      await load();
      toast.success(t('magazyn.items.deleteSuccess'));
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : t('magazyn.items.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  if (forbidden) {
    return <p className="text-muted-foreground py-10 text-center">{t('errors.accessDenied')}</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('magazyn.items.searchPlaceholder')}
            className="pl-9"
          />
        </div>
        <Button size="sm" onClick={openAdd}>
          <Plus className="size-4" />
          {t('magazyn.items.addItem')}
        </Button>
      </div>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{t('magazyn.items.columns.code')}</TableHead>
              <TableHead>{t('magazyn.items.columns.color')}</TableHead>
              <TableHead>{t('magazyn.items.columns.name')}</TableHead>
              <TableHead>{t('magazyn.items.columns.project')}</TableHead>
              <TableHead>{t('magazyn.items.columns.invoiceDescription')}</TableHead>
              <TableHead className="text-right">{t('magazyn.items.columns.unitPriceNet')}</TableHead>
              <TableHead className="w-[100px]">{t('magazyn.items.columns.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="h-32 text-center">
                  <p className="font-medium">{t('magazyn.items.empty.title')}</p>
                  <p className="text-muted-foreground text-sm">{t('magazyn.items.empty.description')}</p>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.code}</TableCell>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <span
                        className="border-input inline-block size-4 shrink-0 rounded-full border"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-muted-foreground font-mono text-xs">{item.color}</span>
                    </span>
                  </TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.projectName ? (
                      <span className="flex items-center gap-2">
                        {item.projectColor && (
                          <span
                            className="inline-block size-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: item.projectColor }}
                          />
                        )}
                        {item.projectName}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-[220px] truncate" title={item.invoiceDescription ?? undefined}>
                    {item.invoiceDescription ?? '—'}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {item.unitPriceNet !== null ? item.unitPriceNet.toFixed(2) : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('magazyn.items.editItem')}
                        onClick={() => openEdit(item)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive hover:text-destructive"
                        aria-label={t('common.delete')}
                        onClick={() => setDeleteTarget(item)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/edit — one Dialog for both, editingItem selects the mode. */}
      <Dialog
        open={formOpen}
        onOpenChange={(open) => {
          if (!open && !submitting) {
            setFormOpen(false);
            setEditingItem(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingItem ? t('magazyn.items.editItem') : t('magazyn.items.addItem')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="item-code">{t('magazyn.items.fields.code')}</Label>
                <Input
                  id="item-code"
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="item-color">{t('magazyn.items.fields.color')}</Label>
                {/* Ten sam picker co kolor projektu (ProjectDialog): natywny input + hex. */}
                <div className="flex items-center gap-2">
                  <input
                    id="item-color"
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(form.color) ? form.color : '#000000'}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    className="border-input h-9 w-10 cursor-pointer rounded border p-1"
                  />
                  <Input
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    className="flex-1 font-mono"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="item-name">{t('magazyn.items.fields.name')}</Label>
              <Input
                id="item-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="item-project">{t('magazyn.items.fields.project')}</Label>
                <Select
                  value={form.projectId || NO_PROJECT}
                  onValueChange={(value) =>
                    setForm((f) => ({ ...f, projectId: value === NO_PROJECT ? '' : value }))
                  }
                >
                  <SelectTrigger id="item-project" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PROJECT}>
                      <span className="text-muted-foreground">{t('magazyn.items.noProject')}</span>
                    </SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        <span className="flex items-center gap-2">
                          {project.color && (
                            <span
                              className="inline-block size-2.5 shrink-0 rounded-full"
                              style={{ backgroundColor: project.color }}
                            />
                          )}
                          {project.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="item-price">{t('magazyn.items.fields.unitPriceNet')}</Label>
                <Input
                  id="item-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.unitPriceNet}
                  onChange={(e) => setForm((f) => ({ ...f, unitPriceNet: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="item-invoice-description">{t('magazyn.items.fields.invoiceDescription')}</Label>
              <Input
                id="item-invoice-description"
                value={form.invoiceDescription}
                onChange={(e) => setForm((f) => ({ ...f, invoiceDescription: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting && <Loader2 className="size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('magazyn.items.deleteConfirmTitle', { code: deleteTarget?.code ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('magazyn.items.deleteConfirmDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={deleting} variant="destructive" onClick={handleDelete}>
              {deleting && <Loader2 className="size-4 animate-spin" />}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
